import { TCards } from '@lbs/shared-rp-types'
import { makeAutoObservable, reaction } from 'mobx'
import { interpret } from 'xstate'
import { BoxStore } from './Box.store'
import { boxDecisionMachine } from './boxDecision.machine'

export enum EBettingBoxLayoutMode {
  INITIAL = 'INITIAL', // оба пустые
  SELECTED = 'SELECTED', // активный
  COMPACT = 'COMPACT', // неактивный, пустой
  STAKED = 'STAKED', // неактивный, со ставкой
}

export class BoxUIStore {
  boxStore: BoxStore

  insuranceTemp: number = 0

  private decisionMachine = interpret(boxDecisionMachine)

  reactionDisposers: (() => void)[] = []

  constructor(boxStore: BoxStore) {
    makeAutoObservable(
      this,
      { boxStore: false, decisionMachine: false } as any,
      { autoBind: true }
    )

    this.boxStore = boxStore
    this.decisionMachine.start()

    this.reactionDisposers.push(
      reaction(
        () => this.boxStore.isPendingCardRequest,
        (isPending) => {
          if (isPending) this.decisionMachine.send('PENDING_CARDS_STARTED')
          else this.decisionMachine.send('PENDING_CARDS_RESOLVED')
        }
      )
    )

    // Успешный patch от сервера (без pendingCards) → снимаем транзитный lock.
    // Событие обрабатывается только в состоянии submitting, в остальных игнорируется.
    this.reactionDisposers.push(
      reaction(
        () => this.boxStore.lastSuccessfulActionPatchId,
        () => {
          this.decisionMachine.send('SUBMISSION_RESOLVED')
        }
      )
    )

    // Keep local decision UI state aligned with active box ownership.
    // When the box loses turn, reset transient UI modes (insurance/switch/sixCard) to idle.
    this.reactionDisposers.push(
      reaction(
        () => this.boxStore.isMyTurn,
        (isMyTurn) => {
          if (!isMyTurn) this.decisionMachine.send('CANCEL')
        }
      )
    )
  }

  get service() {
    return this.decisionMachine
  }

  // Вызывается из RussianPokerStore при error-ответе от сервера.
  // Снимает транзитный lock состояния submitting.
  onActionError = (): void => {
    this.decisionMachine.send('ACTION_ERROR')
  }

  dispose = (): void => {
    this.decisionMachine.stop()
    this.reactionDisposers.forEach((dispose) => dispose())
  }

  patch = (payload: Partial<BoxUIStore>): void => {
    Object.assign(this, payload)
  }

  toggleCardsForSwitch = (card: TCards): void => {
    this.decisionMachine.send({ type: 'TOGGLE_CARD', card })
  }

  setSixCardDecision = (): void => {
    this.decisionMachine.send('SET_SIX_CARD')
  }

  setInsuranceDecision = (): void => {
    this.decisionMachine.send('SET_INSURANCE')
  }

  sendConfirm = (): void => {
    this.decisionMachine.send('CONFIRM')
  }

  sendCancel = (): void => {
    this.decisionMachine.send('CANCEL')
  }

  get layout(): EBettingBoxLayoutMode {
    const { boxes } = this.boxStore.rootStore
    const { hasStake, selected } = this.boxStore

    if (selected) return EBettingBoxLayoutMode.SELECTED

    const noHasStakes = boxes.every((box) => !box.hasStake)
    const hasSelectedBox = boxes.some((box) => box.selected)

    if (noHasStakes && !hasSelectedBox) return EBettingBoxLayoutMode.INITIAL

    if (!selected) {
      if (hasStake) return EBettingBoxLayoutMode.STAKED
      return EBettingBoxLayoutMode.COMPACT
    }

    return EBettingBoxLayoutMode.INITIAL
  }
}
